<template>
  <div>
    <v-row no-gutters class="mb-2">
      <v-col cols="6" class="d-flex align-center">
        <h2 class="color-custom-blue font-weight-600 m-0">Meetings</h2>
      </v-col>
      <v-col cols="6" class="text-right">
        <span v-if="getPermission('lead:view')">
          <v-skeleton-loader
            v-if="tableLoading"
            class="custom-skeleton d-inline-block"
            type="button"
            height="28"
            width="140"
          />
          <v-btn
            v-else
            depressed
            class="custom-bold-button white--text"
            color="cyan"
            small
            @click.stop="openMeetingDrawer"
          >
            <v-icon dark left small>mdi-plus</v-icon>
            Create Meeting
          </v-btn>
        </span>
      </v-col>
    </v-row>

    <v-row class="customer-detail">
      <v-col md="12" sm="12" class="pt-0">
        <v-skeleton-loader
          class="custom-skeleton"
          v-if="tableLoading"
          type="table-tbody"
        ></v-skeleton-loader>
        <v-simple-table
          v-else
          class="simple-table"
          height="calc(100vh - 270px)"
        >
          <template v-slot:default>
            <thead>
              <tr style="background-color: #f3f6f9; color: #24326d">
                <th class="simple-table-th pl-2">Barcode</th>
                <th class="simple-table-th pl-2">Title</th>
                <th class="simple-table-th pl-2">Meeting Type</th>
                <th class="simple-table-th pl-2">Date</th>
                <th class="simple-table-th pl-2">Start Time</th>
                <th class="simple-table-th pl-2">Duration</th>
                <th class="simple-table-th pl-2">Location / URL</th>
                <th class="simple-table-th pl-2">Assigned To</th>
                <th class="simple-table-th text-center pl-2">Attachments</th>
                <th class="simple-table-th text-center pl-2">Action</th>
              </tr>
            </thead>
            <tbody class="custom-border-bottom custom-border-top">
              <template v-if="meetings.length > 0">
                <tr
                  v-for="item in meetings"
                  :key="item.id"
                  class="alternate-listing-row cursor-pointer"
                  @click="openEditMeeting(item)"
                >
                  <td
                    class="simple-table-td font-size-14 font-weight-500 text-capitalize"
                  >
                    <v-chip color="blue" outlined small class="font-weight-600">
                      <template>{{ item.barcode }}</template>
                    </v-chip>
                  </td>
                  <td
                    class="simple-table-td font-size-14 font-weight-500 text-capitalize"
                  >
                    {{ item.title || "—" }}
                  </td>
                  <td
                    class="simple-table-td font-size-14 font-weight-500 text-capitalize"
                  >
                    {{ item.meeting_type || "—" }}
                  </td>
                  <td class="simple-table-td font-size-14 font-weight-500">
                    {{ formatMeetingDate(item.date) }}
                  </td>
                  <td class="simple-table-td font-size-14 font-weight-500">
                    {{ item.start_time || "—" }}
                  </td>
                  <td class="simple-table-td font-size-14 font-weight-500">
                    {{ formatDuration(item.duration) }}
                  </td>
                  <td class="simple-table-td font-size-14 font-weight-500">
                    <a
                      v-if="isMeetingUrl(item.meeting_location)"
                      :href="item.meeting_location"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-break"
                      @click.stop
                    >
                      {{ item.meeting_location }}
                    </a>
                    <template v-else>
                      {{ item.meeting_location || "—" }}
                    </template>
                  </td>
                  <td
                    class="simple-table-td font-size-14 font-weight-500 text-capitalize"
                  >
                    <v-chip
                      v-if="item.assignee && item.assignee.display_name"
                      color="green"
                      outlined
                      small
                      class="font-weight-600"
                    >
                      <template>{{
                        (item.assignee && item.assignee.display_name) || "—"
                      }}</template>
                    </v-chip>
                    <em v-else class="text-muted">No assignee</em>
                  </td>
                  <td class="simple-table-td text-center">
                    <v-btn
                      :disabled="pageLoading"
                      icon
                      color="blue"
                      small
                      :aria-label="`View attachments for ${item.barcode}`"
                      @click.stop="viewAttachment(item)"
                    >
                      <v-icon>mdi-paperclip</v-icon>
                    </v-btn>
                  </td>
                  <td class="simple-table-td text-center">
                    <v-btn
                      v-if="getPermission('lead:update')"
                      :disabled="pageLoading"
                      icon
                      color="cyan"
                      small
                      :aria-label="`Edit meeting ${item.barcode}`"
                      @click.stop="openEditMeeting(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="getPermission('lead:delete')"
                      :disabled="pageLoading || deleteLoading"
                      icon
                      color="red"
                      small
                      :aria-label="`Delete meeting ${item.barcode}`"
                      @click.stop="openDeleteMeeting(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>

              <tr v-else-if="!tableLoading" class="row-not-found">
                <td colspan="10" class="text-center py-4">
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  <p class="text-muted m-0">
                    Uhh... There are no meetings at the moment.
                  </p>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <ListingFooter
          :dataLoading="tableLoading"
          :showingFrom="paginationStart"
          :showingTo="paginationEnd"
          :totalRows="totalRows"
          :currentPage="page"
          :totalPages="totalPages"
        ></ListingFooter>
      </v-col>
    </v-row>

    <v-navigation-drawer
      v-model="openDialog"
      app
      temporary
      right
      stateless
      width="600"
      style="z-index: 100"
    >
      <v-card flat class="d-flex flex-column h-100">
        <v-card-title
          class="headline dialog-custom-header text-uppercase custom-border-bottom custom-header-blue-text white-background"
        >
          <span style="font-size: 18px">
            {{ isEditing ? "Edit Meeting" : "Create Meeting" }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closeMeetingDrawer">
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="flex-grow-1 px-4 py-3">
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            style="height: calc(100vh - 140px); position: relative"
          >
            <v-form ref="meetingForm" v-model="formValid" lazy-validation>
              <div class="px-1 pt-2">
                <!-- Title -->
                <div class="form-group mb-4">
                  <label
                    class="font-size-14 font-weight-500 mb-1 d-block required"
                  >
                    Title
                  </label>
                  <v-text-field
                    v-model.trim="meeting.title"
                    :rules="[requiredRule]"
                    dense
                    filled
                    solo
                    flat
                    hide-details="auto"
                    placeholder="Enter Title"
                    color="cyan"
                    :maxlength="100"
                  ></v-text-field>
                </div>

                <!-- Meeting Type -->
                <div class="form-group mb-4">
                  <label class="font-size-14 font-weight-500 m-0">
                    Meeting Type
                  </label>

                  <div class="d-flex align-center">
                    <v-select
                      :menu-props="{ offsetY: true }"
                      v-model="meeting.meeting_type"
                      :items="meetingList"
                      dense
                      filled
                      solo
                      flat
                      hide-details
                      placeholder="Select Meeting Type"
                      color="cyan"
                      item-color="cyan"
                    ></v-select>
                    <v-btn
                      text
                      color="cyan"
                      class="p-0 font-weight-600"
                      @click="BrandData"
                    >
                      <v-icon small>mdi-cog</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Date & Start Time -->
                <v-row dense class="form-group mb-3">
                  <v-col cols="12" sm="6" class="pt-0 pb-1">
                    <label
                      class="font-size-14 font-weight-500 mb-1 d-block required"
                    >
                      Date
                    </label>
                    <DatePicker
                      solo
                      mandatory
                      :pageLoading="pageLoading"
                      placeholder="Select Date"
                      v-model="meeting.date"
                    ></DatePicker>
                  </v-col>
                  <v-col cols="12" sm="6" class="pt-0 pb-1">
                    <label
                      class="font-size-14 font-weight-500 mb-1 d-block required"
                    >
                      Start Time
                    </label>
                    <v-select
                      v-model="meeting.start_time"
                      :items="timeOptions"
                      :rules="[requiredRule]"
                      dense
                      filled
                      solo
                      flat
                      hide-details="auto"
                      placeholder="Select Time"
                      color="cyan"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Meeting Summary Alert -->
                <v-alert
                  dense
                  outlined
                  type="info"
                  color="cyan"
                  class="font-size-13 mb-4"
                  icon="mdi-information-outline"
                >
                  This meeting will take place on
                  <strong>{{ meetingSummary }}</strong>
                </v-alert>

                <!-- Location / URL -->
                <div class="form-group mb-4">
                  <label
                    class="font-size-14 font-weight-500 mb-1 d-block required"
                  >
                    Location / URL
                  </label>
                  <v-text-field
                    v-model.trim="meeting.meeting_location"
                    :rules="[requiredRule]"
                    dense
                    filled
                    solo
                    flat
                    hide-details="auto"
                    placeholder="Enter Location / Meeting URL"
                    color="cyan"
                    :maxlength="100"
                  ></v-text-field>
                </div>

                <!-- Assigned To -->
                <div class="form-group mb-4">
                  <label
                    class="font-size-14 font-weight-500 mb-1 d-block required"
                  >
                    Assigned To
                  </label>
                  <v-select
                    :menu-props="{ offsetY: true }"
                    v-model="meeting.assign_to"
                    :items="assigneeList"
                    :rules="[requiredRule]"
                    item-text="display_name"
                    item-value="id"
                    dense
                    filled
                    solo
                    flat
                    hide-details="auto"
                    placeholder="Select Assignee"
                    color="cyan"
                    item-color="cyan"
                  ></v-select>
                </div>

                <!-- Description -->
                <div class="form-group mb-4">
                  <label class="font-size-14 font-weight-500 mb-1 d-block">
                    Description
                  </label>
                  <v-textarea
                    v-model.trim="meeting.description"
                    filled
                    solo
                    flat
                    dense
                    hide-details
                    rows="3"
                    placeholder="Enter Description..."
                    color="cyan"
                    :maxlength="500"
                  ></v-textarea>
                </div>

                <!-- Attachment -->
                <div class="form-group mb-3">
                  <label class="font-size-14 font-weight-500 mb-1 d-block">
                    Attachment
                  </label>
                  <FileUpload v-model="files"></FileUpload>
                </div>
              </div>
            </v-form>
          </perfect-scrollbar>
        </v-card-text>

        <v-divider class="my-0"></v-divider>
        <v-card-actions class="d-flex justify-content-end px-4 py-3 bg-white">
          <v-btn
            depressed
            outlined
            color="grey darken-1"
            class="custom-grey-border custom-bold-button text-capitalize mr-2"
            @click="closeMeetingDrawer"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="cyan"
            class="custom-bold-button white--text px-5 text-capitalize"
            :disabled="!formValid"
            :loading="pageLoading"
            @click="saveMeeting"
          >
            <v-icon small left>mdi-check</v-icon>
            {{ isEditing ? "Update" : "Save" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>

    <!-- Manage Meeting Type Dialog -->
    <ManageMeetingType
      :b-dialog="manageMeetingDialog"
      :brand="meetingList"
      @close-dialog="manageMeetingDialog = false"
      @get-product-brand="getOptions"
    ></ManageMeetingType>

    <!-- Close Confirmation Dialog -->
    <Dialog :commonDialog="closeConfirmDialog" :dialog-width="600">
      <template v-slot:body>
        <v-layout>
          <v-flex md2 class="text-center">
            <inline-svg
              width="50"
              :src="$assetURL('media/custom-svg/attention-circle.svg')"
            />
          </v-flex>
          <v-flex md10>
            <p class="font-weight-700 font-size-16 mb-2">
              Are you sure you want to leave this page?
            </p>
            <p class="text-muted font-size-13 mb-4">
              You have unsaved changes that will be lost if you close this
              drawer.
            </p>
            <div class="d-flex">
              <v-btn
                depressed
                class="mr-2 custom-grey-border custom-bold-button white--text text-capitalize"
                color="cyan"
                small
                @click="closeConfirmDialog = false"
              >
                Stay on this page
              </v-btn>
              <v-btn
                depressed
                class="custom-grey-border custom-bold-button text-capitalize"
                small
                @click="confirmCloseMeetingDrawer"
              >
                Leave this page
              </v-btn>
            </div>
          </v-flex>
        </v-layout>
      </template>
    </Dialog>

    <!-- View Attachment Dialog -->
    <Dialog :commonDialog="viewAttachmentDialog" :dialog-width="1000">
      <template v-slot:title>View All Attachments</template>
      <template v-slot:body>
        <AdditionalDocuments
          v-if="attachmentData && attachmentData.id"
          :type-id="attachmentData.id"
          class="mx-4"
          type-text="Meeting Attachments"
          :type="35"
          :extensive="false"
        ></AdditionalDocuments>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          @click="closeAttachmentDialog"
        >
          Close
        </v-btn>
      </template>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmTemplate :delete-dialog="deleteDialog">
      <template v-slot:title>Delete Confirmation</template>
      <template v-slot:text>
        <h3 class="font-size-16 font-weight-500 delete-text">
          Once you delete
          <span class="font-weight-700">
            {{ deletingMeeting ? deletingMeeting.barcode : "" }}
          </span>
          , you won't be able to retrieve it later.<br />
          Are you sure you want to delete this meeting?
        </h3>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          :disabled="deleteLoading"
          class="mx-2 custom-grey-border custom-bold-button"
          @click="closeDeleteMeeting"
        >
          No! Cancel
        </v-btn>
        <v-btn
          depressed
          :disabled="deleteLoading"
          :loading="deleteLoading"
          color="red lighten-1 white--text"
          class="mx-2 custom-grey-border custom-bold-button"
          @click="deleteMeeting"
        >
          Yes! Delete
        </v-btn>
      </template>
    </DeleteConfirmTemplate>
  </div>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import { DELETE, POST, QUERY } from "@/core/services/store/request.module";
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import FileUpload from "@/view/components/app-component/FileInput.vue";
import ManageMeetingType from "@/view/pages/partials/Manage-Meeting-Type.vue";
import DeleteConfirmTemplate from "@/view/pages/partials/Delete.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import AdditionalDocuments from "@/view/pages/engineer/Additional-Documents";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import { PaginationEventBus } from "@/core/lib/pagination.lib";

export default {
  name: "MeetingDetails",
  title: "Meeting",
  mixins: [CommonMixin],
  components: {
    DatePicker,
    Dialog,
    FileUpload,
    ManageMeetingType,
    DeleteConfirmTemplate,
    AdditionalDocuments,
    ListingFooter,
  },

  props: {
    type: {
      type: Number,
      default: 1,
    },
    parentId: {
      type: [Number, String],
      default: null,
    },
    assigneeList: {
      type: [Array, Object],
      default: () => [],
    },
    relatedTo: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      openDialog: false,
      closeConfirmDialog: false,
      manageMeetingDialog: false,
      viewAttachmentDialog: false,
      formValid: false,
      pageLoading: false,
      tableLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      deletingMeeting: null,
      page: 1,
      paginationPageSize: 10,
      totalPages: 0,
      totalRows: 0,
      rowsOffset: 0,
      meetingList: new Array(),
      attachmentData: null,
      meetingData: new Array(),
      meetings: [],
      files: [
        {
          file: null,
          name: null,
        },
      ],
      dateMenu: false,
      relatedToOptions: ["Opportunity", "Lead", "Customer"],
      meetingTypes: [
        "F2F Client Office",
        "F2F Our Office",
        "Online Meeting",
        "Phone Call",
      ],
      reminderOptions: [
        "At time of meeting",
        "5 minutes before",
        "10 minutes before",
        "15 minutes before",
        "30 minutes before",
        "1 hour before",
        "1 day before",
      ],
      durationOptions: [
        { text: "15 Min", minutes: 15 },
        { text: "30 Min", minutes: 30 },
        { text: "45 Min", minutes: 45 },
        { text: "1 Hour", minutes: 60 },
        { text: "1 Hour 30 Min", minutes: 90 },
        { text: "2 Hours", minutes: 120 },
      ],

      meeting: this.defaultMeeting(),
      attachments: [{ id: 1, file: null, name: "" }],
      attachmentId: 1,
      requiredRule: (value) => !!value || "This field is required",
    };
  },

  computed: {
    isEditing() {
      return Boolean(this.meeting.id);
    },

    normalizedParentId() {
      const id = Number(this.parentId);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    paginationStart() {
      return this.meetings.length ? this.rowsOffset + 1 : 0;
    },

    paginationEnd() {
      return this.rowsOffset + this.meetings.length;
    },

    timeOptions() {
      const options = [];
      for (let hour = 0; hour < 24; hour += 1) {
        for (let minute = 0; minute < 60; minute += 30) {
          const period = hour >= 12 ? "PM" : "AM";
          const displayHour = hour % 12 || 12;
          options.push(
            `${String(displayHour).padStart(2, "0")}:${String(minute).padStart(
              2,
              "0"
            )} ${period}`
          );
        }
      }
      return options;
    },

    formattedDate() {
      if (!this.meeting.date) return "";
      const [year, month, day] = this.meeting.date.split("-");
      return `${day}/${month}/${year}`;
    },

    meetingSummary() {
      if (!this.meeting.date || !this.meeting.start_time) {
        return "selected date and time";
      }

      const start = this.toMinutes(this.meeting.start_time);
      const date = new Date(`${this.meeting.date}T00:00:00`);
      const dateLabel = new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);
      return `${dateLabel} ${this.fromMinutes(start)}`;
    },
  },

  mounted() {
    PaginationEventBus.$on("update:pagination", this.handlePaginationUpdate);
    this.getOptions();
    this.getMeetings();
  },

  beforeDestroy() {
    PaginationEventBus.$off("update:pagination", this.handlePaginationUpdate);
  },

  watch: {
    parentId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.page = 1;
        this.getMeetings();
      }
    },
  },

  methods: {
    BrandData() {
      this.manageMeetingDialog = true;
      this.getOptions();
    },
    defaultMeeting() {
      const now = new Date();
      const localDate = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 10);

      return {
        relatedTo: "Opportunity",
        title: "",
        meeting_type: "F2F Client Office",
        date: localDate,
        start_time: "08:30 AM",
        duration: 30,
        reminder: null,
        meeting_location: "",
        assign_to: null,
        description: "",
      };
    },
    getOptions() {
      this.$store
        .dispatch(QUERY, {
          url: "meeting/options",
        })
        .then(({ data }) => {
          this.meetingList = data?.meeting_types;
        })
        .catch((error) => {
          this.meetings = [];
          this.logError(error);
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },

    getMeetings() {
      if (!this.normalizedParentId) {
        this.meetings = [];
        this.totalRows = 0;
        this.totalPages = 0;
        this.page = 1;
        return Promise.resolve();
      }

      this.tableLoading = true;
      return this.$store
        .dispatch(QUERY, {
          url: `meeting/${this.normalizedParentId}`,
          data: {
            type: this.type,
            current_page: this.page,
            per_page: this.paginationPageSize,
          },
        })
        .then(({ data }) => {
          this.meetings = Array.isArray(data?.rows) ? data.rows : [];
          this.page = data?.current_page || 1;
          this.totalRows = data?.total_rows || 0;
          this.paginationPageSize = data?.per_page || this.paginationPageSize;
          this.totalPages = this.paginationPageSize
            ? Math.ceil(this.totalRows / this.paginationPageSize)
            : 0;
          this.rowsOffset = data?.offset || 0;
        })
        .catch((error) => {
          this.meetings = [];
          this.logError(error);
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },

    handlePaginationUpdate(page) {
      if (!page || page === this.page) return;
      this.page = page;
      this.getMeetings();
    },

    openMeetingDrawer() {
      this.meeting = this.defaultMeeting();
      this.resetAttachments();
      this.resetMeetingValidation();
      this.closeConfirmDialog = false;
      this.openDialog = true;
    },
    viewAttachment(item) {
      this.attachmentData = item;
      this.$nextTick(() => {
        this.viewAttachmentDialog = true;
      });
    },
    closeAttachmentDialog() {
      this.viewAttachmentDialog = false;
      this.attachmentData = null;
    },
    openEditMeeting(item) {
      if (!this.getPermission("lead:update")) return;

      this.meeting = {
        id: item.id,
        relatedTo: item.relatedTo || "Opportunity",
        title: item.title || "",
        meeting_type: item.meeting_type || "F2F Client Office",
        date: item.date ? String(item.date).slice(0, 10) : "",
        start_time: item.start_time || "",
        duration: Number(item.duration) || 30,
        reminder: item.reminder || null,
        meeting_location: item.meeting_location || "",
        assign_to: item.assign_to ? Number(item.assign_to) : null,
        description: item.description || "",
      };
      this.resetAttachments();
      this.resetMeetingValidation();
      this.closeConfirmDialog = false;
      this.openDialog = true;
    },

    openDeleteMeeting(item) {
      if (!this.getPermission("lead:delete")) return;
      this.deletingMeeting = item;
      this.deleteDialog = true;
    },

    closeDeleteMeeting() {
      if (this.deleteLoading) return;
      this.deleteDialog = false;
      this.deletingMeeting = null;
    },

    deleteMeeting() {
      if (!this.deletingMeeting || this.deleteLoading) return;

      this.deleteLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `meeting/${this.deletingMeeting.id}`,
        })
        .then(() => {
          this.deleteDialog = false;
          this.deletingMeeting = null;
          return this.getMeetings();
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },

    closeMeetingDrawer() {
      if (this.pageLoading) return;
      this.closeConfirmDialog = true;
    },

    confirmCloseMeetingDrawer() {
      this.dismissMeetingDrawer();
    },

    dismissMeetingDrawer() {
      this.closeConfirmDialog = false;
      this.openDialog = false;
      this.meeting = this.defaultMeeting();
      this.resetAttachments();
      this.resetMeetingValidation();
    },

    resetAttachments() {
      this.attachmentId = 1;
      this.attachments = [{ id: 1, file: null, name: "" }];
      this.files = [];
    },

    resetMeetingValidation() {
      this.$nextTick(() => {
        if (this.$refs.meetingForm) {
          this.$refs.meetingForm.resetValidation();
        }
      });
    },

    saveMeeting() {
      if (!this.$refs.meetingForm.validate()) return;

      const formData = new FormData();
      const meetingData = {
        ...this.meeting,
        parent: this.normalizedParentId,
        type: this.type,
      };

      Object.keys(meetingData).forEach((key) => {
        const value = meetingData[key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      this.files.forEach((file, index) => {
        if (file && file.file) {
          formData.append(`file[${index}][file]`, file.file);
          formData.append(`file[${index}][name]`, file.name || file.file.name);
        }
      });

      this.pageLoading = true;

      this.$store
        .dispatch(POST, {
          url: "meeting",
          data: formData,
        })
        .then(() => {
          this.dismissMeetingDrawer();
          this.page = 1;
          this.getMeetings();
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },

    toMinutes(value) {
      const match = value.match(/(\d+):(\d+)\s(AM|PM)/);
      if (!match) return 0;
      let hour = Number(match[1]) % 12;
      if (match[3] === "PM") hour += 12;
      return hour * 60 + Number(match[2]);
    },

    fromMinutes(value) {
      const normalized = value % (24 * 60);
      const hour = Math.floor(normalized / 60);
      const minute = normalized % 60;
      const period = hour >= 12 ? "PM" : "AM";
      return `${String(hour % 12 || 12).padStart(2, "0")}:${String(
        minute
      ).padStart(2, "0")} ${period}`;
    },

    addAttachment() {
      this.attachmentId += 1;
      this.attachments.push({
        id: this.attachmentId,
        file: null,
        name: "",
      });
    },

    removeAttachment(index) {
      if (this.attachments.length === 1) {
        this.attachments.splice(0, 1, {
          id: this.attachments[0].id,
          file: null,
          name: "",
        });
        return;
      }
      this.attachments.splice(index, 1);
    },

    openFilePicker(index) {
      const input = this.$refs[`fileInput-${index}`];
      const element = Array.isArray(input) ? input[0] : input;
      if (element) element.click();
    },

    selectFile(event, index) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.attachments[index].file = file;
      if (!this.attachments[index].name) {
        this.attachments[index].name = file.name;
      }
    },

    formatMeetingDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    },

    formatDuration(value) {
      if (!value) return "—";
      const minutes = Number(value);
      if (!Number.isFinite(minutes)) return value;
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes
        ? `${hours} hr ${remainingMinutes} min`
        : `${hours} hr`;
    },

    isMeetingUrl(value) {
      return /^https?:\/\//i.test(value || "");
    },
  },
};
</script>

<style scoped>
/* Standard Vuetify and Bootstrap classes used */
</style>
